<template>
  <div class="tab-content">
    <div class="app-download-section">
      <div class="hiking-image d-none d-md-block">
        <img :src="hikingImage2" :alt="$t('download_modal.send_to_device_tab.hiker_with_gps_device')" />
      </div>

      <div class="gps-options">
        <h2 class="display-5 mb-3"><strong>{{ $t('send_to_device.title') }}</strong></h2>

        <div class="device-options">
          <button
            v-if="isUserLoggedIn"
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleGPSDevice(device.id)"
            :disabled="isBusy(device.id)"
          >
            <div class="device-info">
              <img v-if="device.id === 'garmin'" style="max-width: 40px" :src="garminConnect" :alt="$t('download_modal.send_to_device_tab.connect_to_garmin')" />
              <img v-else-if="device.id === 'hiiker'" class="device-logo" :src="hiikerAppIcon" alt="" />
              <span v-else class="material-icons device-icon" :aria-label="$t('suunto.send.connect_to_suunto')">watch</span>
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'hiiker'" class="connection-status">{{ $t('send_to_device.hiiker.description') }}</span>
                <span v-else class="connection-status">
                  {{ isConnected(device.id) ? $t('download_modal.send_to_device_tab.connected') : $t('download_modal.send_to_device_tab.not_connected') }}
                </span>
              </div>
            </div>
            <button
              v-if="!isConnected(device.id) && !isBusy(device.id)"
              class="connect-button"
              @click.stop="handleGPSDevice(device.id)"
            >
              {{ $t('download_modal.send_to_device_tab.connect') }}
            </button>
            <span
              v-else-if="!isBusy(device.id)"
              class="material-icons text-yellow"
            >
              keyboard_arrow_right
            </span>
            <div v-else class="spinner-small"></div>
          </button>

          <button
            v-else
            v-for="device in gpsDevices"
            :key="device.id"
            class="device-option"
            @click="handleLogin"
          >
            <div class="device-info">
              <img v-if="device.id === 'garmin'" style="max-width: 40px" :src="garminConnect" :alt="$t('download_modal.send_to_device_tab.connect_to_garmin_2')" />
              <img v-else-if="device.id === 'hiiker'" class="device-logo" :src="hiikerAppIcon" alt="" />
              <span v-else class="material-icons device-icon" :aria-label="$t('suunto.send.connect_to_suunto')">watch</span>
              <div class="device-text">
                <span>{{ device.name }}</span>
                <span v-if="device.id === 'hiiker'" class="connection-status">{{ $t('send_to_device.hiiker.description') }}</span>
                <span v-else class="connection-status">
                  {{ $t('download_modal.send_to_device_tab.not_connected') }}
                </span>
              </div>
            </div>
            <span class="d-flex align-items-center">
              <span class="mr-1" style="width: 95px; line-height: 1;"><small class="text-muted" style="font-size: .75em">{{ $t('download_modal.send_to_device_tab.sign_in_to_sync') }}</small></span>
              <span class="material-icons text-muted">keyboard_arrow_right</span>
            </span>
          </button>
        </div>

        <div v-if="appNotice === 'install'" class="app-notice">
          <p class="mb-1"><strong>{{ $t('send_to_device.hiiker.install_title') }}</strong></p>
          <p class="small mb-2">{{ $t('send_to_device.hiiker.install_body') }}</p>
          <div class="store-badges">
            <a href="https://apps.apple.com/app/hiiker/id1470447885" target="_blank" rel="noopener noreferrer">
              <img :src="appStoreBadge" :alt="$t('download_modal.sendto_phone_tab.download_on_the_app_store')" />
            </a>
            <a href="https://play.google.com/store/apps/details?id=com.hiiker" target="_blank" rel="noopener noreferrer">
              <img :src="googlePlayBadge" :alt="$t('download_modal.sendto_phone_tab.get_it_on_google_play')" />
            </a>
          </div>
        </div>
        <p v-else-if="appNotice === 'notifications_off'" class="app-notice small mb-0">
          {{ $t('send_to_device.hiiker.notifications_off') }}
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue';
import graphClient from '../../utils/graphql/graph_client';
import hikingImage2 from '../../images/download-modal-3.webp';
import garminConnect from '../../images/garmin-connect.webp';
import hiikerAppIcon from '../../images/app-icon-hiiker.svg';
import appStoreBadge from '../../images/app-app-store-btn-white.png';
import googlePlayBadge from '../../images/google-play-btn-white.png';
import { OnBoardingGoal } from "../../utils/models/OnBoardingGoal";
import { SEND_TO_GARMIN } from '../../utils/graphql/send-to-garmin';
import { SEND_TO_SUUNTO } from '../../utils/graphql/send-to-suunto';
import { SEND_TO_HIIKER_APP } from '../../utils/graphql/send-to-hiiker-app';
import { sendDevices, sendToAppOutcome, type SendToAppOutcome } from './devices';
import { suuntoConnectUrl } from './suunto';
import { createGarminConnector } from '../../utils/helpers/connectGarminHelper';
import { getDownloadResourceContext, getResourceNoun } from './context';
import { i18n } from '../../i18n';

const t = (key: string): string => i18n.global.t(key);

// Props
const props = defineProps<{
  userData: any;
}>();

// Emits
const emit = defineEmits<{
  close: [];
}>();

const isUserLoggedIn = computed(() => props.userData != null);

const resourceContext = computed(() => getDownloadResourceContext());
const resourceNoun = computed(() => getResourceNoun(resourceContext.value.graphqlType));

// One whole sentence per resource type, so each language can inflect the noun.
const idNotFoundMessage = () => {
  switch (resourceNoun.value) {
    case 'recording':
      return t('download_modal.send_to_device_tab.recording_id_not_found');
    case 'hike':
      return t('download_modal.send_to_device_tab.hike_id_not_found');
    default:
      return t('download_modal.send_to_device_tab.trail_id_not_found');
  }
};

const sentToGarminMessage = () => {
  switch (resourceNoun.value) {
    case 'recording':
      return t('download_modal.send_to_device_tab.recording_sent_to_garmin');
    case 'hike':
      return t('download_modal.send_to_device_tab.hike_sent_to_garmin');
    default:
      return t('download_modal.send_to_device_tab.trail_sent_to_garmin');
  }
};

const sendFailedMessage = () => {
  switch (resourceNoun.value) {
    case 'recording':
      return t('download_modal.send_to_device_tab.failed_to_send_recording');
    case 'hike':
      return t('download_modal.send_to_device_tab.failed_to_send_hike');
    default:
      return t('download_modal.send_to_device_tab.failed_to_send_trail');
  }
};

const sentToSuuntoMessage = () => {
  switch (resourceNoun.value) {
    case 'recording':
      return t('suunto.send.recording_sent');
    case 'hike':
      return t('suunto.send.hike_sent');
    default:
      return t('suunto.send.trail_sent');
  }
};

const suuntoSendFailedMessage = () => {
  switch (resourceNoun.value) {
    case 'recording':
      return t('suunto.send.failed_to_send_recording');
    case 'hike':
      return t('suunto.send.failed_to_send_hike');
    default:
      return t('suunto.send.failed_to_send_trail');
  }
};

const handleLogin = () => {
  if (window.onBoardingVue && window.onBoardingVue.launchFor) {
    window.onBoardingVue.launchFor(OnBoardingGoal.ConnectToApps);
  }
};

// Data
const isSendingToGarmin = ref(false);
const isConnectingToGarmin = ref(false);
const garminConnector = createGarminConnector();

const isSendingToSuunto = ref(false);
const isSendingToApp = ref(false);
// Shown under the devices when the push couldn't reach a phone.
const appNotice = ref<SendToAppOutcome | null>(null);

const gpsDevices = computed(() =>
  sendDevices(Boolean((window as any).suuntoAvailable || props.userData?.suunto_available))
);

// Computed
const isGarminConnected = computed(() => props.userData?.garmin_connected || false);
const isSuuntoConnected = computed(() => props.userData?.suunto_connected || false);

// The HiiKER app needs no connecting: it's the account the user is signed in with.
const isConnected = (device: string) => {
  if (device === 'hiiker') return true;
  return device === 'suunto' ? isSuuntoConnected.value : isGarminConnected.value;
};
const isBusy = (device: string) => {
  if (device === 'hiiker') return isSendingToApp.value;
  return device === 'suunto' ? isSendingToSuunto.value : isSendingToGarmin.value || isConnectingToGarmin.value;
};

// Methods
const updateGarminConnectionStatus = () => {
  if (props.userData) {
    props.userData.garmin_connected = true;
  }

  if (window.user) {
    window.user.garmin_connected = true;
  }
};

const sendResourceToGarmin = async () => {
  try {
    isSendingToGarmin.value = true;

    const context = resourceContext.value;
    const resourceId = context.id;

    if (!resourceId) {
      alert(idNotFoundMessage());
      return;
    }

    const response = await graphClient.request(SEND_TO_GARMIN, {
      klass: context.graphqlType,
      id: resourceId
    });

    if (response.sendToGarmin) {
      alert(sentToGarminMessage());
      emit('close');
    } else {
      alert(sendFailedMessage());
    }
  } catch (error: any) {
    console.error('Error sending to Garmin:', error);
    alert(error.response?.errors?.[0]?.message || sendFailedMessage());
  } finally {
    isSendingToGarmin.value = false;
  }
};

const sendResourceToSuunto = async () => {
  const context = resourceContext.value;
  if (!context.id) {
    alert(idNotFoundMessage());
    return;
  }

  try {
    isSendingToSuunto.value = true;
    const response = await graphClient.request(SEND_TO_SUUNTO, { klass: context.graphqlType, id: context.id });

    if (response.sendToSuunto) {
      alert(sentToSuuntoMessage());
      emit('close');
    } else {
      alert(suuntoSendFailedMessage());
    }
  } catch (error: any) {
    console.error('Error sending to Suunto:', error);
    alert(error.response?.errors?.[0]?.message || suuntoSendFailedMessage());
  } finally {
    isSendingToSuunto.value = false;
  }
};

const sendResourceToApp = async () => {
  const context = resourceContext.value;
  if (!context.id) {
    alert(idNotFoundMessage());
    return;
  }

  appNotice.value = null;
  try {
    isSendingToApp.value = true;
    const response = await graphClient.request(SEND_TO_HIIKER_APP, { klass: context.graphqlType, id: context.id });
    const outcome = sendToAppOutcome(response.sendToHiikerApp?.status);

    if (outcome === 'sent') {
      alert(t('send_to_device.hiiker.sent'));
      emit('close');
    } else if (outcome === 'failed') {
      alert(t('send_to_device.hiiker.failed'));
    } else {
      appNotice.value = outcome;
    }
  } catch (error: any) {
    console.error('Error sending to the HiiKER app:', error);
    alert(error.response?.errors?.[0]?.message || t('send_to_device.hiiker.failed'));
  } finally {
    isSendingToApp.value = false;
  }
};

const handleGPSDevice = async (device: string) => {
  if (device === 'hiiker') {
    await sendResourceToApp();
    return;
  }

  if (device === 'suunto') {
    if (isSuuntoConnected.value) {
      await sendResourceToSuunto();
    } else {
      window.location.href = suuntoConnectUrl(window.location.pathname + window.location.search);
    }
    return;
  }

  if (!isGarminConnected.value) {
    try {
      isConnectingToGarmin.value = true;
      await garminConnector.connect();
      updateGarminConnectionStatus();
      await sendResourceToGarmin();
    } catch (error: any) {
      const message = error?.message || t('download_modal.send_to_device_tab.failed_to_connect_to_garmin');
      alert(message);
    } finally {
      isConnectingToGarmin.value = false;
    }

    return;
  }

  await sendResourceToGarmin();
};

// Clean up on unmount
onUnmounted(() => {
  garminConnector.cancel();
});
</script>

<style scoped lang="scss">
.tab-content {
  animation: fadeIn 0.3s ease-in-out;
  padding: 2em;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.app-download-section {
  display: flex;
  gap: 32px;
  align-items: flex-start;
}

.hiking-image {
  flex: 0 0 300px;

  img {
    width: 100%;
    height: auto;
    border-radius: 8px;
  }
}

.gps-options {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  h2 {
    font-size: 24px;
    margin-bottom: 32px;
    font-weight: 600;
  }
}

// Shown under the devices when the push to the HiiKER app couldn't reach a phone.
.app-notice {
  margin-top: 16px;
  padding: 12px 16px;
  border-radius: 8px;
  background-color: rgba(255, 255, 255, 0.06);
}

.store-badges {
  display: flex;
  gap: 8px;

  img {
    height: 36px;
    width: auto;
  }
}

.device-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.device-option {
  background-color: transparent;
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 16px 24px;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.3);
  }

  .device-info {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .device-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .device-logo {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }

  .device-icon {
    width: 40px;
    font-size: 32px;
    text-align: center;
  }

  .connection-status {
    font-size: 10px;
    color: rgba(255, 255, 255, 0.6);
  }

  .arrow-icon {
    color: rgba(255, 255, 255, 0.5);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.spinner-small {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: #00a8e8;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.connect-button {
  background-color: #00a8e8;
  color: white;
  border: none;
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0090cc;
  }

  &:active {
    background-color: #0080bb;
  }
}

@media (max-width: 768px) {
  .app-download-section {
    flex-direction: column;
  }

  .hiking-image {
    flex: none;
    height: 200px;
  }
}
</style>
